import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { IPD_COLORS } from '../../theme';
import {
  contextFieldSx,
  quickActionSx,
  heroBannerSx,
  filterBarSx,
  summaryHighlightSx,
  alertBannerSx,
  pharmacyTabBarSx,
  pharmacyTabSx,
} from './pharmacyStyles';

export function PharmacyHero({ icon: Icon, title, subtitle, actions }) {
  return (
    <Box sx={heroBannerSx}>
      {Icon && (
        <Box sx={{
          width: 52,
          height: 52,
          borderRadius: '14px',
          backgroundColor: 'rgba(255,255,255,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 28, color: '#fff' }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, zIndex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 20 }, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.82)', mt: 0.5, lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, zIndex: 1, flexWrap: 'wrap' }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

export function QuickActionCard({ icon: Icon, label, description, tone = 'teal', onClick }) {
  const toneColor = {
    teal: IPD_COLORS.primary,
    mint: IPD_COLORS.success.text,
    amber: IPD_COLORS.warning.text,
    red: IPD_COLORS.error.text,
  }[tone] || IPD_COLORS.primary;

  return (
    <Box onClick={onClick} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
      sx={quickActionSx(toneColor)}>
      <Box className="pharmacy-action-icon" sx={{
        width: 40,
        height: 40,
        borderRadius: '10px',
        backgroundColor: `${toneColor}14`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.25s ease',
      }}>
        {Icon && <Icon sx={{ color: toneColor, fontSize: 20 }} />}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: IPD_COLORS.textPrimary, lineHeight: 1.3 }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mt: 0.35, lineHeight: 1.4 }}>
            {description}
          </Typography>
        )}
      </Box>
      <ChevronRightIcon sx={{ color: IPD_COLORS.textMuted, fontSize: 20, mt: 0.5, flexShrink: 0 }} />
    </Box>
  );
}

export function EmrContextPanel({ patientId, admissionId, doctorId, title = 'Patient Context' }) {
  const fields = [
    { label: 'Patient', value: patientId, icon: PersonOutlineIcon },
    { label: 'Admission', value: admissionId, icon: LocalHospitalIcon },
    { label: 'Doctor', value: doctorId, icon: MedicalServicesIcon },
  ];

  return (
    <Box sx={{
      ...summaryHighlightSx,
      position: { md: 'sticky' },
      top: { md: 16 },
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.textPrimary, mb: 1.5 }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {fields.map(({ label, value, icon: FieldIcon }) => (
          <Box key={label} sx={contextFieldSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.35 }}>
              <FieldIcon sx={{ fontSize: 15, color: IPD_COLORS.primary }} />
              <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, fontWeight: 600, letterSpacing: 0.3 }}>
                {label}
              </Typography>
            </Box>
            <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14, fontWeight: value ? 600 : 400 }}>
              {value || '—'}
            </Typography>
          </Box>
        ))}
      </Box>
      {!patientId && (
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted, mt: 1.5, lineHeight: 1.5 }}>
          Open from a patient chart to auto-fill context via URL parameters.
        </Typography>
      )}
    </Box>
  );
}

export function PharmacyFilterBar({ children, sx = {} }) {
  return (
    <Box sx={{ ...filterBarSx, ...sx }}>
      {children}
    </Box>
  );
}

function patientAlertTone(severity) {
  const normalized = String(severity || 'MODERATE').toUpperCase();
  if (normalized === 'SEVERE') return IPD_COLORS.error;
  if (normalized === 'MILD') return {
    bg: IPD_COLORS.selectedBg,
    border: IPD_COLORS.selectedBorder,
    text: IPD_COLORS.primary,
  };
  return IPD_COLORS.warning;
}

export function PatientClinicalAlertsBox({ alerts = [], patientName }) {
  const hasAlerts = alerts.length > 0;

  return (
    <Box sx={{
      ...summaryHighlightSx,
      mb: 2,
      borderColor: hasAlerts ? IPD_COLORS.error.border : IPD_COLORS.success.border,
      background: hasAlerts
        ? `linear-gradient(160deg, ${IPD_COLORS.error.bg} 0%, ${IPD_COLORS.cardBg} 72%)`
        : `linear-gradient(160deg, ${IPD_COLORS.success.bg} 0%, ${IPD_COLORS.cardBg} 72%)`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: hasAlerts ? 1.5 : 0 }}>
        <Box sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          backgroundColor: hasAlerts ? `${IPD_COLORS.error.text}16` : `${IPD_COLORS.success.text}16`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          {hasAlerts
            ? <WarningAmberIcon sx={{ fontSize: 18, color: IPD_COLORS.error.text }} />
            : <CheckCircleOutlineIcon sx={{ fontSize: 18, color: IPD_COLORS.success.text }} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: IPD_COLORS.textPrimary, lineHeight: 1.3 }}>
            Patient Alerts
          </Typography>
          {patientName && (
            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.15 }}>
              {patientName}
            </Typography>
          )}
        </Box>
        {hasAlerts && (
          <Box sx={{
            px: 0.85,
            py: 0.2,
            borderRadius: '999px',
            backgroundColor: IPD_COLORS.error.bg,
            color: IPD_COLORS.error.text,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.5,
          }}>
            {alerts.length}
          </Box>
        )}
      </Box>

      {hasAlerts ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
          {alerts.map((alert) => {
            const tone = patientAlertTone(alert.severity);
            return (
              <Box
                key={alert.id}
                sx={{
                  px: 1.15,
                  py: 0.95,
                  borderRadius: '9px',
                  backgroundColor: tone.bg,
                  border: `1px solid ${tone.border}`,
                  borderLeft: `3px solid ${tone.text}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: tone.text, lineHeight: 1.35 }}>
                    {alert.allergen}
                  </Typography>
                  <Typography sx={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: tone.text,
                    textTransform: 'uppercase',
                    letterSpacing: 0.45,
                    flexShrink: 0,
                    mt: 0.1,
                  }}>
                    {alert.severity}
                  </Typography>
                </Box>
                {(alert.reaction || alert.message) && (
                  <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textSecondary, mt: 0.35, lineHeight: 1.45 }}>
                    {alert.reaction || alert.message}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.success.text, lineHeight: 1.5 }}>
          No known allergies or clinical warnings on file.
        </Typography>
      )}
    </Box>
  );
}

export function PharmacyAlertBanner({ icon: Icon, title, message, tone = 'error', onClick }) {
  const palette = tone === 'error' ? IPD_COLORS.error : IPD_COLORS.warning;
  return (
    <Box onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      sx={alertBannerSx(tone)}>
      {Icon && (
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          backgroundColor: `${palette.text}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon sx={{ color: palette.text, fontSize: 20 }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: palette.text }}>{title}</Typography>
        {message && (
          <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mt: 0.25 }}>{message}</Typography>
        )}
      </Box>
      {onClick && <ChevronRightIcon sx={{ color: palette.text, flexShrink: 0 }} />}
    </Box>
  );
}

export function SummaryStat({ label, value, highlight }) {
  return (
    <Box sx={{
      textAlign: 'center',
      px: 2,
      py: 1.5,
      borderRadius: '10px',
      backgroundColor: highlight ? IPD_COLORS.selectedBg : IPD_COLORS.panelHeaderBg,
      border: `1px solid ${highlight ? IPD_COLORS.selectedBorder : IPD_COLORS.border}`,
    }}>
      <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: highlight ? 22 : 18, fontWeight: 700, color: highlight ? IPD_COLORS.primary : IPD_COLORS.textPrimary, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

export function DetailSummaryGrid({ children }) {
  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {children}
    </Grid>
  );
}

export function PharmacyTabBar({ tabs, activePath, onNavigate }) {
  return (
    <Box sx={pharmacyTabBarSx} role="tablist" aria-label="Pharmacy sections">
      {tabs.map((tab) => {
        const disabled = Boolean(tab.disabled);
        const active = !disabled && (activePath === tab.path || activePath.startsWith(`${tab.path}/`));
        const Icon = tab.icon;
        return (
          <Box
            key={tab.path}
            role="tab"
            aria-selected={active}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (!disabled) onNavigate(tab.path);
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNavigate(tab.path);
              }
            }}
            sx={pharmacyTabSx(active, disabled)}
          >
            {Icon && <Icon sx={{ fontSize: 18 }} />}
            {tab.label}
            {!disabled && tab.badge != null && tab.badge > 0 && (
              <Box component="span" sx={{
                ml: 0.5,
                px: 0.75,
                py: 0.15,
                borderRadius: '8px',
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: active ? IPD_COLORS.primary : IPD_COLORS.textSecondary,
                color: '#fff',
                lineHeight: 1.4,
              }}>
                {tab.badge}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
